import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { safeConfirm } from '../../lib/confirm';
import { Clock, Stethoscope, Timer, CheckCircle2, Trash2 } from 'lucide-react';
import { HoursModal } from './AdminClinicians';
import { wallDateTime } from '../../lib/time';

// Iter 136 — dedicated sidebar page for clinician working hours, time
// blocks and appointment buffers (previously buried inside Clinicians).
export default function AdminClinicianHours() {
  const [clinicians, setClinicians] = useState([]);
  const [hoursFor, setHoursFor] = useState(null);
  const [buffers, setBuffers] = useState({});   // user_id -> minutes
  const [savedFor, setSavedFor] = useState(null);
  const [blocksSummary, setBlocksSummary] = useState({}); // user_id -> upcoming count

  const load = async () => {
    const r = await api.get('/clinicians');
    const list = r.data || [];
    setClinicians(list);
    const buf = {};
    const blk = {};
    await Promise.all(list.map(async c => {
      try {
        const b = await api.get(`/clinicians/${c.user_id}/buffer`);
        buf[c.user_id] = b.data?.buffer_min ?? '';
      } catch (_) { buf[c.user_id] = ''; }
      try {
        const bl = await api.get(`/clinicians/${c.user_id}/blocks`);
        const now = new Date().toISOString();
        blk[c.user_id] = (bl.data || []).filter(x => (x.ends_at || '') >= now).length;
      } catch (_) { blk[c.user_id] = 0; }
    }));
    setBuffers(buf);
    setBlocksSummary(blk);
  };
  useEffect(() => { load(); }, []);

  const saveBuffer = async (c) => {
    try {
      await api.put(`/clinicians/${c.user_id}/buffer`, { buffer_min: parseInt(buffers[c.user_id] || 0, 10) });
      setSavedFor(c.user_id);
      setTimeout(() => setSavedFor(null), 2000);
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    }
  };

  // Inactive/archived staff clutter the list — allow permanent removal here.
  const removeInactive = async (c) => {
    if (!(await safeConfirm(`Permanently delete ${c.name}?\n\nThis removes the inactive account, their availability and schedule blocks. Past visit notes keep their name on record.\n\nThis cannot be undone.`))) return;
    try {
      await api.delete(`/users/${c.user_id}`);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Delete failed.');
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-5xl" data-testid="clinician-hours-page">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">SCHEDULING</div>
        <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight flex items-center gap-3">
          <Clock className="w-8 h-8 text-brand"/> Clinician hours & blocks
        </h1>
        <p className="text-sm text-fg-muted mt-2">
          Weekly working hours, time off / blocks, and the buffer between back-to-back appointments — per clinician.
        </p>
      </div>

      <div className="space-y-3">
        {clinicians.map(c => (
          <div key={c.user_id} className="admin-card flex flex-wrap items-center gap-4" data-testid={`hours-row-${c.user_id}`}>
            <div className="w-10 h-10 rounded-full bg-[#F2EBDB] flex items-center justify-center shrink-0">
              {c.photo_url
                ? <img src={c.photo_url} alt={c.name} className="w-10 h-10 rounded-full object-cover"/>
                : <Stethoscope className="w-5 h-5 text-brand"/>}
            </div>
            <div className="flex-1 min-w-[180px]">
              <div className="font-heading font-semibold">
                {c.name}
                {(c.archived || c.active === false) && (
                  <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#B83A3A]/10 text-[#B83A3A]">inactive</span>
                )}
              </div>
              <div className="text-xs text-fg-muted">
                {c.credentials || '—'}
                {blocksSummary[c.user_id] > 0 && (
                  <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#FFF4D6] text-[#8A5A00]">
                    {blocksSummary[c.user_id]} upcoming block(s)
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1.5 text-xs">
              <Timer className="w-3.5 h-3.5 text-fg-muted"/>
              <span className="text-fg-muted">Buffer</span>
              <input type="number" min="0" max="120" step="5"
                     className="input !py-1 !px-2 !text-xs w-16"
                     value={buffers[c.user_id] ?? ''}
                     onChange={e => setBuffers(b => ({ ...b, [c.user_id]: e.target.value }))}
                     data-testid={`buffer-input-${c.user_id}`}/>
              <span className="text-fg-muted">min</span>
              <button onClick={() => saveBuffer(c)} className="btn-secondary !py-1 !px-2 text-xs" data-testid={`buffer-save-${c.user_id}`}>
                {savedFor === c.user_id ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600"/> : 'Save'}
              </button>
            </div>
            <button onClick={() => setHoursFor(c)} className="btn-primary text-sm inline-flex items-center gap-1.5"
                    data-testid={`edit-hours-${c.user_id}`}>
              <Clock className="w-4 h-4"/> Hours & blocks
            </button>
            {(c.archived || c.active === false) && (
              <button onClick={() => removeInactive(c)}
                      className="btn-secondary p-2 text-[#B83A3A]" title="Delete inactive clinician"
                      data-testid={`delete-inactive-${c.user_id}`}>
                <Trash2 className="w-4 h-4"/>
              </button>
            )}
          </div>
        ))}
        {!clinicians.length && (
          <div className="text-sm text-fg-muted py-10 text-center border border-dashed border-soft rounded-lg">
            No clinicians yet — add one under Clinicians first.
          </div>
        )}
      </div>

      {hoursFor && <HoursModal clinician={hoursFor} onClose={() => { setHoursFor(null); load(); }}/>}
    </div>
  );
}
